import { MutableRefObject, useEffect, useRef, useState } from "react";
import { extend, Extent, buffer, createEmpty, getCenter } from "ol/extent";
import { Vector as VectorSource, TileWMS, XYZ, ImageArcGISRest } from "ol/source";
import TileGrid from "ol/tilegrid/TileGrid";
import { Tile as TileLayer, Vector as VectorLayer, Image as ImageLayer } from "ol/layer";
import { Feature, View, Map } from "ol";
import { Point, SimpleGeometry, Geometry } from "ol/geom";
import { defaults as defaultControls } from "ol/control";
import { getDrawingSymbol, getHighLightSymbol, LVMRoadStyle } from "./symbols";
import { ITimberFlowFormMapProps } from "../Providers/mapProvider";
import { LocationButton } from "./Buttons/GeoLocation";
import { HomeButton } from "./Buttons/Home";
import { AttributionButton } from "./Buttons/Attribution";
import { config, IBasemapTileWMS, IBasemapTileXYZ } from "./config";
import { useAppContext } from "../Contexts/AppContext";
import { DrawType } from "./enums";
import { LayersButton } from "./Buttons/Layers";
import { ScaleControl } from "./Buttons/Scale";
import { DrawingButton } from "./Buttons/Drawing";
import { useRegistrationContext } from "../Contexts/RegistrationContext";
import { getPointResolution } from "ol/proj";
import { identify, loadSourceFeatures } from "./rest";
import { tile as tileStrategy } from "ol/loadingstrategy";
import { createXYZ } from "ol/tilegrid";

const DEFAULT_DPI = 25.4 / 0.28;
const inchesPerMeter = 1000 / 25.4;

const tileGrid = new TileGrid(config.default.tileGrid);

let _map: MutableRefObject<Map>;
let sourceRef: MutableRefObject<VectorSource>;
let polygonSourceRef: MutableRefObject<VectorSource>;
let highLightSourceRef: MutableRefObject<VectorSource>;
let hoveredRef: MutableRefObject<Feature>;
let drawTypeRef: MutableRefObject<DrawType>;

export const useMap = (target: MutableRefObject<HTMLDivElement>, props: ITimberFlowFormMapProps): Map => {
    _map = useRef<Map>();
    sourceRef = useRef<VectorSource>(new VectorSource());
    polygonSourceRef = useRef<VectorSource>(new VectorSource());
    highLightSourceRef = useRef<VectorSource>(new VectorSource());
    hoveredRef = useRef<Feature>();

    const {
        isMobile,
        hasMap,
        basemap,
        zoomToSourceFeatures,
        locateFeature,
        setZoomToSourceFeatures,
        toggleLayersMenuVisible,
        getStoredSettings,
        setClickedFeatures,
        drawType,
        setIdentifiedFeatures
    } = useAppContext();

    const { drawnFeatures, setDrawingMenuVisible } = useRegistrationContext();

    drawTypeRef = useRef<DrawType>(drawType);

    useEffect(() => {
        if (target.current && hasMap) {
            const baseLayers = config.basemaps.map((b) => {
                if (b.type === "TileXYZ") {
                    const basemap = b as IBasemapTileXYZ;
                    return new TileLayer({
                        source: new XYZ({
                            url: basemap.source.url + "/tile/{z}/{y}/{x}",
                            maxZoom: basemap.source.maxZoom,
                            projection: basemap.source.projection,
                            tileGrid,
                            crossOrigin: basemap.source.crossOrigin
                        }),
                        visible: getStoredSettings().basemap
                            ? basemap.id === getStoredSettings().basemap
                            : basemap.visible,
                        properties: { id: basemap.id },
                        zIndex: 0
                    });
                }
                if (b.type === "TileWMS") {
                    const basemap = b as IBasemapTileWMS;
                    return new TileLayer({
                        source: new TileWMS({
                            url: basemap.source.url,
                            params: basemap.source.params,
                            projection: basemap.source.projection,
                            crossOrigin: basemap.source.crossOrigin
                        }),
                        visible: basemap.id === getStoredSettings().basemap,
                        properties: { id: basemap.id },
                        zIndex: 0
                    });
                }
            });

            const dataLayers = config.layers
                .filter((layer) => getStoredSettings().layers.includes(layer.id))
                .map((l) => {
                    if (l.type === "TileXYZ") {
                        return new TileLayer({
                            source: new XYZ({
                                url: l.url + "/tile/{z}/{y}/{x}",
                                projection: config.default.projection,
                                crossOrigin: l.crossOrigin,
                                tileGrid
                            }),
                            minResolution: l.minResolution,
                            maxResolution: l.maxResolution,
                            extent: l.extent,
                            properties: { id: l.id },
                            zIndex: l.zIndex
                        });
                    }
                    if (l.type === "ImageArcGISRest") {
                        return new ImageLayer({
                            properties: { id: l.id },
                            source: new ImageArcGISRest({
                                url: l.url,
                                crossOrigin: l.crossOrigin,
                                attributions: l.copyright,
                                params: l.params
                            }),
                            zIndex: l.zIndex
                        });
                    }
                    if (l.type === "Vector") {
                        const source = new VectorSource({
                            loader: (ext) => loadSourceFeatures(l.url, ext, source),
                            strategy: tileStrategy(createXYZ({ tileSize: 512 }))
                        });
                        return new VectorLayer({
                            properties: { id: l.id },
                            source,
                            minResolution: l.minResolution,
                            maxResolution: l.maxResolution,
                            zIndex: l.zIndex,
                            style: LVMRoadStyle
                        });
                    }
                })
                .filter((l) => l);

            const highLightLayer = new VectorLayer({
                properties: { id: config.default.highLightLayerId },
                source: highLightSourceRef.current,
                zIndex: 90,
                style: getHighLightSymbol
            });

            const drawingLayerSource = new VectorSource();

            const drawingLayer = new VectorLayer({
                properties: { id: config.default.drawingLayerId },
                source: drawingLayerSource,
                zIndex: 100,
                style: (drawFeature) => getDrawingSymbol(drawFeature.get("DRAWTYPE"), drawFeature.get("MASTERNAME"))
            });

            _map.current = new Map({
                layers: [...baseLayers, ...dataLayers, highLightLayer, drawingLayer],
                target: target.current,
                view: new View({
                    projection: config.default.projection,
                    center: config.default.center,
                    extent: config.default.viewExtent,
                    zoom: config.default.zoom,
                    enableRotation: false,
                    minZoom: config.default.minZoom,
                    maxZoom: config.default.maxZoom
                }),
                controls: defaultControls({ zoom: false, rotate: false, attribution: false }).extend([
                    new LocationButton(),
                    new HomeButton(),
                    new LayersButton(toggleLayersMenuVisible),
                    new AttributionButton(),
                    new ScaleControl(),
                    new DrawingButton(setDrawingMenuVisible)
                ])
            });

            _map.current.on("moveend", calculateScale);
            _map.current.on("pointermove", onPointerMove);
            _map.current.on("click", onMapClick);
            _map.current.once("postcompose", () =>
                drawnFeatures && drawnFeatures.length ? zoomToExtent(drawingLayerSource.getExtent()) : zoomHome()
            );

            if (drawnFeatures && drawnFeatures.length) {
                drawnFeatures.map((f) => f.setStyle(getDrawingSymbol(f.get("DRAWTYPE"), f.get("MASTERNAME"))));
                drawingLayerSource.addFeatures(drawnFeatures);
            }
        }
    }, [hasMap]);

    const onPointerMove = (evt) => {
        if (evt.dragging) {
            return;
        }

        if (hoveredRef.current) {
            hoveredRef.current.set("HOVERED", false);
            hoveredRef.current = null;
        }
        if (drawTypeRef.current === DrawType.PolylineLvmRoad) {
            _map.current.forEachFeatureAtPixel(
                evt.pixel,
                (feature, layer) => {
                    layer.get("id") === "roadsLVM" && (feature as Feature).set("DRAWTYPE", DrawType.PolylineLvmRoad);
                    (feature as Feature).set("HOVERED", true);
                    hoveredRef.current = feature as Feature;
                    return true;
                },
                { layerFilter: (layer) => layer.get("id") === "roadsLVM", hitTolerance: 10 }
            );
        }

        const hasFeatureAtPixel = _map.current.hasFeatureAtPixel(evt.pixel, {
            layerFilter: (layer) =>
                drawTypeRef.current === DrawType.PolylineLvmRoad
                    ? true
                    : layer.get("id") !== "roadsLVM" && layer.get("id") !== config.default.highLightLayerId,
            hitTolerance: 20
        });
        target.current.style.cursor = hasFeatureAtPixel ? "pointer" : null;
    };

    const onMapClick = (evt) => {
        if (evt.dragging) {
            return;
        }

        console.log("map click");
        isMobile && !drawTypeRef.current && setDrawingMenuVisible(false);

        if (drawTypeRef.current === DrawType.PolygonProperty) {
            identifyOnMap(evt);
            return;
        }

        const clicked = _map.current.getFeaturesAtPixel(evt.pixel, {
            layerFilter: (layer) =>
                drawTypeRef.current === DrawType.PolylineLvmRoad ? true : layer.get("id") !== "roadsLVM",
            hitTolerance: 20
        });
        if (!clicked.length) {
            setClickedFeatures(null);
            return;
        }

        _map.current.forEachFeatureAtPixel(
            evt.pixel,
            (feature, layer) => {
                layer &&
                    layer.get("id") === "roadsLVM" &&
                    (feature as Feature).set("DRAWTYPE", DrawType.PolylineLvmRoad);
                (feature as Feature).set("CLICKED", true);
            },
            {
                layerFilter: (layer) =>
                    drawTypeRef.current === DrawType.PolylineLvmRoad ? true : layer.get("id") !== "roadsLVM",
                hitTolerance: 20
            }
        );
        setClickedFeatures(clicked as Feature<Geometry>[]);
    };

    const identifyOnMap = async (evt) => {
        const coords = _map.current.getEventCoordinate(evt.originalEvent);
        const mapSize = _map.current.getSize();
        const extent = _map.current.getView().calculateExtent(mapSize);
        if (!coords || !mapSize || !extent) {
            return;
        }
        const params = {
            f: "json",
            tolerance: 8,
            imageDisplay: mapSize.toString() + ",96",
            geometry: `{"x":${coords[0]}, "y":${coords[1]}}`,
            geometryType: "esriGeometryPoint",
            sr: "3059",
            mapExtent: extent.toString(),
            layers: "visible:" + config.identify.layers.toString()
        };

        setIdentifiedFeatures(await identify(config.identify.url, params));
    };

    useEffect(() => {
        if (props.features && props.features.length) {
            sourceRef.current.clear();
            sourceRef.current.addFeatures(props.features);
            polygonSourceRef.current.clear();
            polygonSourceRef.current.addFeatures(props.features);
        } else {
            sourceRef.current.clear();
            polygonSourceRef.current.clear();
        }
    }, [props.forceUpdate, props.features]);

    useEffect(() => {
        _map.current &&
            _map.current.once("postcompose", () => {
                zoomToSourceFeatures === 1 && zoomHome(() => setZoomToSourceFeatures(null));
                zoomToSourceFeatures > 1 &&
                    zoomToExtent(sourceRef.current.getExtent(), () => setZoomToSourceFeatures(null));
            });
    }, [zoomToSourceFeatures]);

    useEffect(() => {
        if (!locateFeature || !locateFeature.feature) {
            highLightSourceRef.current.clear();
            return;
        }
        highLightSourceRef.current.clear();
        highLightSourceRef.current.addFeature(locateFeature.feature);
        zoomToFeature(locateFeature.feature);
    }, [locateFeature]);

    useEffect(() => {
        drawTypeRef.current = drawType;
    }, [drawType]);

    useEffect(() => {
        _map.current &&
            _map.current
                .getLayers()
                .getArray()
                .map(
                    (layer) =>
                        config.basemaps.find((bmap) => bmap.id === layer.get("id")) &&
                        layer.setVisible(basemap === layer.get("id"))
                );

        saveBaseMapSettings(basemap);
    }, [basemap]);

    //@ts-ignore
    window.map = _map.current;
    return _map.current;
};

const calculateScale = () => {
    const view = _map.current.getView();
    const pointResolution = getPointResolution(view.getProjection(), view.getResolution(), view.getCenter(), "m");
    //@ts-ignore
    document.mapScale = Math.round(parseFloat(pointResolution.toString()) * inchesPerMeter * DEFAULT_DPI);
};

const zoomHome = (callback?: (arg0: boolean) => void) => {
    const view = _map.current.getView();
    //@ts-ignore
    const resolutionX = config.default.extentSize[0] / view.viewportSize_[0];
    //@ts-ignore
    const resolutionY = config.default.extentSize[1] / view.viewportSize_[1];
    const zoom = view.getZoomForResolution(resolutionX > resolutionY ? resolutionX : resolutionY);

    view.fit(new Point(config.default.center), {
        maxZoom: zoom,
        duration: 1000,
        callback
    });
};

const zoomToExtent = (extent: Extent, callback?: (arg0: boolean) => void) => {
    if (!extent || extent.includes(Infinity)) {
        return;
    }
    document.dispatchEvent(new Event("stopTracking"));
    const view = _map.current.getView();
    view.fit(extent, { duration: 500, padding: [60, 60, 60, 60], callback });
};

const zoomToFeature = (feature: Feature): void => {
    const view = _map.current.getView();
    const geometry = feature.getGeometry() as SimpleGeometry;
    const currentZoom = view.getZoom();
    const maxZoom = currentZoom > config.default.zoomToLevel ? currentZoom : config.default.zoomToLevel;
    document.dispatchEvent(new Event("stopTracking"));
    view.fit(geometry, { duration: 500, maxZoom });
};

const saveBaseMapSettings = (storedBaseMap: string) => {
    const storage = localStorage.getItem("basemap");
    if (storage && storage === JSON.stringify(storedBaseMap)) {
        return;
    }
    localStorage.setItem("basemap", JSON.stringify(storedBaseMap));
};
